/**
 * PHASE 9 — SeeIt-mode overlay (DOM).
 *
 * Renders the guidance from interact/seeIt.ts on top of the sky:
 *
 *   - object on screen       -> centre reticle + caption, target "found"
 *   - object off screen      -> edge arrow pointing the way to pan
 *   - object below horizon   -> caption explaining it is not visible
 *   - no orientation yet     -> "calibrating" caption
 *
 * The reticle/arrow geometry comes from pure functions (seeItState /
 * arrowScreenPoint) which mirror the renderer's matrices, so the guide is
 * aligned with what is drawn on the sky.
 */

import type { SeeItState } from "../interact/seeIt";
import { arrowScreenPoint } from "../interact/seeIt";

export interface SeeItTargetInfo {
  name: string;
}

export class SeeItOverlay {
  private root: HTMLElement;
  private reticleEl: HTMLElement;
  private arrowEl: HTMLElement;
  private captionEl: HTMLElement;
  private nameEl: HTMLElement;
  private active = false;

  constructor(container: HTMLElement) {
    this.root = container;
    this.reticleEl = container.querySelector<HTMLElement>(".seeit-reticle") ?? document.createElement("div");
    this.arrowEl = container.querySelector<HTMLElement>(".seeit-arrow") ?? document.createElement("div");
    this.captionEl = container.querySelector<HTMLElement>(".seeit-caption") ?? document.createElement("div");
    this.nameEl = container.querySelector<HTMLElement>(".seeit-name") ?? document.createElement("div");
  }

  /** Enter SeeIt-mode for a target. */
  show(target: SeeItTargetInfo): void {
    this.nameEl.textContent = target.name;
    this.active = true;
    this.root.classList.add("visible");
    this.root.setAttribute("role", "status");
  }

  hide(): void {
    this.active = false;
    this.root.classList.remove("visible");
    this.root.removeAttribute("role");
  }

  get isActive(): boolean {
    return this.active;
  }

  /**
   * Per-frame sync. `state` is null while the camera has no orientation.
   */
  sync(state: SeeItState | null, widthPx: number, heightPx: number): void {
    if (!this.active) return;
    if (!state) {
      this.setMode("calibrating");
      this.reticleEl.classList.remove("visible");
      this.arrowEl.classList.remove("visible");
      return;
    }
    if (state.onScreen) {
      this.setMode("center");
      this.reticleEl.classList.add("visible");
      this.arrowEl.classList.remove("visible");
      this.reticleEl.classList.toggle("found", state.offsetRad < 0.12);
      return;
    }
    this.reticleEl.classList.remove("visible");
    if (state.hint === "below-horizon") {
      this.arrowEl.classList.remove("visible");
      this.setMode("below-horizon");
      return;
    }
    this.setMode("pan");
    this.arrowEl.classList.add("visible");
    const p = arrowScreenPoint(widthPx, heightPx, state.screenAngleDeg, 56);
    this.arrowEl.style.transform = `translate(${p.xPx.toFixed(1)}px, ${p.yPx.toFixed(1)}px) rotate(${state.screenAngleDeg.toFixed(0)}deg)`;
  }

  private setMode(mode: "center" | "pan" | "below-horizon" | "calibrating"): void {
    this.root.dataset.mode = mode;
    let caption = "";
    if (mode === "pan") caption = "pan toward the arrow";
    else if (mode === "below-horizon") caption = "below the horizon right now";
    else if (mode === "calibrating") caption = "waiting for orientation…";
    else caption = "centered";
    this.captionEl.textContent = caption;
  }
}